import React, { useEffect, useState } from "react";
import { useAnnouncement } from "../../hooks/useAnnouncement";

const DISMISSED_KEY = "888g_ann_dismissed_at";

function readDismissed() {
  try {
    const v = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
    return isNaN(v) ? 0 : v;
  } catch {
    return 0;
  }
}

function AnnouncementPopup() {
  const ann = useAnnouncement();
  const [open, setOpen] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(readDismissed());

  // Open the popup only when a NEW announcement (with image) arrives
  useEffect(() => {
    if (!ann || !ann.image) return;
    if ((ann.updatedAt || 0) > dismissedAt) {
      setOpen(true);
    }
  }, [ann?.updatedAt, ann?.image, dismissedAt]);

  const close = () => {
    if (ann && ann.updatedAt) {
      try {
        localStorage.setItem(DISMISSED_KEY, String(ann.updatedAt));
      } catch {}
      setDismissedAt(ann.updatedAt);
    }
    setOpen(false);
  };

  if (!open || !ann || !ann.image) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3, 5, 16, 0.9)",
        zIndex: 9800,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0a0e24",
          border: "1px solid var(--cyan-border)",
          borderRadius: 16,
          width: "100%",
          maxWidth: 420,
          maxHeight: "90vh",
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 20px 60px rgba(0, 229, 255, 0.25), 0 0 40px rgba(139, 92, 246, 0.2)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Chrome corner brackets */}
        <span style={{
          position: "absolute", top: -1, left: -1, width: 16, height: 16,
          borderLeft: "2px solid var(--cyan)", borderTop: "2px solid var(--cyan)",
          borderTopLeftRadius: 16, pointerEvents: "none",
        }} />
        <span style={{
          position: "absolute", top: -1, right: -1, width: 16, height: 16,
          borderRight: "2px solid var(--cyan)", borderTop: "2px solid var(--cyan)",
          borderTopRightRadius: 16, pointerEvents: "none",
        }} />
        <span style={{
          position: "absolute", bottom: -1, left: -1, width: 16, height: 16,
          borderLeft: "2px solid var(--cyan)", borderBottom: "2px solid var(--cyan)",
          borderBottomLeftRadius: 16, pointerEvents: "none",
        }} />
        <span style={{
          position: "absolute", bottom: -1, right: -1, width: 16, height: 16,
          borderRight: "2px solid var(--cyan)", borderBottom: "2px solid var(--cyan)",
          borderBottomRightRadius: 16, pointerEvents: "none",
        }} />

        {/* Close button */}
        <button
          onClick={close}
          aria-label="Close"
          style={{
            position: "absolute", top: 10, right: 10, zIndex: 2,
            width: 30, height: 30, borderRadius: "50%",
            background: "rgba(0, 0, 0, 0.6)",
            border: "1px solid var(--cyan-border)",
            color: "var(--cyan)",
            cursor: "pointer",
            fontSize: 18,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "var(--font-body)",
          }}
        >
          ×
        </button>

        {/* Header ribbon */}
        <div style={{
          padding: "10px 14px",
          background: "linear-gradient(90deg, rgba(0, 229, 255, 0.12) 0%, rgba(139, 92, 246, 0.12) 100%)",
          borderBottom: "1px solid var(--cyan-border)",
          fontFamily: "var(--font-display)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.15em",
          color: "var(--cyan)",
          textTransform: "uppercase",
        }}>
          Announcement
        </div>

        {/* Image */}
        <div style={{
          width: "100%",
          background: "#050815",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          maxHeight: "55vh",
          overflow: "hidden",
        }}>
          <img
            src={ann.image}
            alt="Announcement"
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "55vh",
              objectFit: "contain",
              display: "block",
            }}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        </div>

        {/* Text */}
        {ann.text && (
          <div style={{
            padding: "14px 18px",
            fontSize: 13,
            lineHeight: 1.5,
            color: "var(--text-primary)",
            maxHeight: 120,
            overflowY: "auto",
          }}>
            {ann.text}
          </div>
        )}

        {/* Footer button */}
        <div style={{ padding: "12px 14px 14px", borderTop: "1px solid var(--border)" }}>
          <button
            onClick={close}
            style={{
              width: "100%",
              padding: "12px",
              background: "var(--grad-primary)",
              color: "white",
              border: "none",
              borderRadius: 10,
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              boxShadow: "0 0 12px rgba(0, 229, 255, 0.4)",
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnnouncementPopup;
